/**
 * Atlas Email Service (Unified)
 * - Emulator: Displays OTP and messages locally (no backend)
 * - Production: Defers to backend (log/ready state)
 */

import { getFirebaseProjectId } from '../utils/firebaseProject';

class EmailService {
  constructor() {
    this.isEmulator = this.detectEmulatorMode();
    this.backendUrl = this.getBackendUrl();
    try {
      // eslint-disable-next-line no-console
      console.log('📧 Email Service initialized', {
        mode: this.isEmulator ? 'EMULATOR' : 'PRODUCTION',
        backendUrl: this.backendUrl
      });
    } catch (_) { /* noop */ }
  }

  detectEmulatorMode() {
    const envEmulator = process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true';
    const isEmulatorEnvironment = process.env.REACT_APP_ENVIRONMENT === 'emulator';
    const isProduction = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENVIRONMENT === 'production';
    return envEmulator && isEmulatorEnvironment && !isProduction;
  }

  getBackendUrl() {
    const projectId = getFirebaseProjectId();
    if (this.isEmulator) {
      return `http://127.0.0.1:5002/${projectId}/asia-south1`;
    }
    return process.env.REACT_APP_BACKEND_URL || `https://asia-south1-${projectId}.cloudfunctions.net`;
  }

  async sendOTPEmail(toEmail, otpCode, userName = 'User') {
    try {
      // eslint-disable-next-line no-console
      console.log('📧 Sending OTP Email...', { toEmail, userName, mode: this.isEmulator ? 'EMULATOR' : 'PRODUCTION' });
      if (this.isEmulator) return this.displayOTPLocally(toEmail, otpCode, userName);
      return this.sendOTPViaBackend(toEmail, otpCode, userName);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Email Service error (sendOTPEmail):', error);
      return { success: false, message: 'Failed to send OTP', method: this.isEmulator ? 'emulator_display' : 'backend_email', error: String(error && error.message ? error.message : error) };
    }
  }

  async displayOTPLocally(toEmail, otpCode, userName) {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('last_otp', otpCode);
        sessionStorage.setItem('last_otp_email', toEmail);
        sessionStorage.setItem('last_otp_timestamp', new Date().toISOString());
      }
      if (typeof alert !== 'undefined') {
        // eslint-disable-next-line no-alert
        alert(`🧪 EMULATOR MODE - OTP Testing\n\n📧 Email: ${toEmail}\n👤 User: ${userName}\n🔐 OTP: ${otpCode}\n\n⏰ Valid for 10 minutes\n\n💡 In production, this will be sent via email.`);
      }
      return { success: true, message: `OTP displayed for ${toEmail} (emulator mode)`, method: 'emulator_display', otpCode, environment: 'emulator' };
    } catch (error) {
      return { success: false, message: 'Failed to display OTP locally', method: 'emulator_display', error: String(error && error.message ? error.message : error) };
    }
  }

  async sendOTPViaBackend(toEmail, otpCode, userName) {
    // eslint-disable-next-line no-console
    console.log('🚀 PRODUCTION MODE: Backend will handle email sending', { toEmail, userName, backendUrl: this.backendUrl });
    return { success: true, message: `Email will be sent to ${toEmail} by backend`, method: 'backend_email', environment: 'production' };
  }

  async sendEmail(toEmail, subject, body, userName = 'User') {
    try {
      // eslint-disable-next-line no-console
      console.log('📧 Sending Email...', { toEmail, subject, mode: this.isEmulator ? 'EMULATOR' : 'PRODUCTION' });
      if (this.isEmulator) {
        if (typeof alert !== 'undefined') {
          // eslint-disable-next-line no-alert
          alert(`🧪 EMULATOR MODE\n\nTo: ${toEmail}\nSubject: ${subject}\n\n${body}`);
        }
        return { success: true, message: `Message displayed for ${toEmail}`, method: 'emulator_display', environment: 'emulator' };
      }
      return { success: true, message: `Message will be sent to ${toEmail} by backend`, method: 'backend_email', environment: 'production' };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Email Service error (sendEmail):', error);
      return { success: false, message: 'Failed to send message', method: this.isEmulator ? 'emulator_display' : 'backend_email', error: String(error && error.message ? error.message : error) };
    }
  }

  async testEmailService() {
    try {
      // eslint-disable-next-line no-console
      console.log('🧪 Testing email service...', { env: this.isEmulator ? 'emulator' : 'production' });
      await this.sendOTPEmail('test@example.com', '123456', 'Test User');
      return { success: true, method: this.isEmulator ? 'emulator_display' : 'backend_email', environment: this.isEmulator ? 'emulator' : 'production', message: `Email service is ready (${this.isEmulator ? 'emulator' : 'production'} mode)` };
    } catch (error) {
      return { success: false, method: this.isEmulator ? 'emulator_display' : 'backend_email', error: String(error && error.message ? error.message : error) };
    }
  }

  getServiceInfo() {
    return { serviceName: 'Atlas Email Service', method: this.isEmulator ? 'emulator_display' : 'backend_email', environment: this.isEmulator ? 'emulator' : 'production', backendUrl: this.backendUrl, isConfigured: true };
  }
}

const emailService = new EmailService();
export default emailService;


